import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import { KeyRound, Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';

export default function ChangePasswordPage() {
  const [params] = useSearchParams();
  const forced = params.get('forced') === '1';
  const navigate = useNavigate();
  const { getAuthHeader, logout, user } = useAuth();

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPwd !== confirmPwd) { setErr('New Password and Confirm Password do not match.'); return; }
    if (newPwd.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
        confirm_password: confirmPwd,
      }, { headers: getAuthHeader() });
      setDone(true);
      // Spec §15 — invalidate current session, force re-login
      setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, 1500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to change password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {!forced && <AppHeader />}
      <main className={`flex-1 flex items-center justify-center px-4 py-10 ${forced ? '' : ''}`}>
        <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center">
              <KeyRound className="w-5 h-5" />
            </div>
            <div>
              <h1 data-testid="change-password-title" className="text-xl font-bold text-white">
                {forced ? 'Change Your Password' : 'Change Password'}
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {forced
                  ? `Signed in as ${user?.name || user?.username}. Set a new password to continue.`
                  : 'Update your account password. You will be signed out after a successful change.'}
              </p>
            </div>
          </div>

          {done ? (
            <div className="space-y-3 text-center py-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
              <p data-testid="change-password-done" className="text-sm text-slate-200">
                Password changed successfully. Redirecting to login…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Current Password</label>
                <input data-testid="cp-current" type="password" autoFocus value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">New Password</label>
                <input data-testid="cp-new" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
                <p className="text-[10px] text-slate-500 mt-1">At least 6 characters. Cannot match your username or be a common password.</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Confirm New Password</label>
                <input data-testid="cp-confirm" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500" />
              </div>
              {err && (
                <div data-testid="cp-error" className="p-2.5 bg-red-950/60 border border-red-800/70 rounded-xl text-red-200 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {err}
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="cp-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 font-semibold text-sm rounded-xl">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Change Password
              </button>
              {!forced && (
                <button type="button" onClick={() => navigate(-1)} className="w-full text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Cancel
                </button>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
